import React, { useState, useCallback, useMemo, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppSelector } from "@redux/hooks";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericSearchbar, { ParsedFilter } from "@ui-components/Searchbar/GenericSearchbar";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { DataStateView } from "kontrolEdge/components/Backbone/BackboneDetails/StateViews";
import { TableSkeleton } from "@ui-components/Skeleton/GenericSkeleton";
import {
    OverviewReflector,
    OverviewCluster,
    BgpSession,
    useReflectorColumns,
    useClusterColumns,
    useBgpSessionColumns,
    filterRows,
} from "./OverviewData";
import {
    fetchOverview,
    shapeReflectors,
    shapeClusters,
    shapeBgpSessions,
    OverviewCounters,
    OverviewType,
} from "./request";
import { useCachedFetch } from "@hooks/useCachedFetch";
import { RefreshingIndicator } from "@shared/components/ListLoading";
import { overviewSectionCacheKey } from "../../helpers/cacheKeys";

/* ------------------------------------------------------------------ */
/*  Summary Card                                                       */
/* ------------------------------------------------------------------ */

const SummaryCard: React.FC<{
    title: string;
    count: number;
    items: { label: string; count: number; variant: "success" | "error" | "warning" | "neutral" }[];
}> = ({ title, count, items }) => (
    <GenericCard isCollapsible={false}>
        <div className="p-4">
            <p className="text-xs text-semantic-text-secondary">{title}</p>
            <p className="text-3xl font-semibold text-semantic-text-primary mt-1">{count}</p>
            <div className="flex items-center gap-3 mt-2 text-xs text-semantic-text-secondary">
                {items.map((item) => (
                    <span key={item.label} className="flex items-center gap-1">
                        <span
                            className={`inline-block w-2 h-2 rounded-full ${
                                item.variant === "success"
                                    ? "bg-semantic-fg-success"
                                    : item.variant === "error"
                                    ? "bg-semantic-fg-danger"
                                    : item.variant === "warning"
                                    ? "bg-semantic-fg-warning"
                                    : "bg-semantic-fg-secondary"
                            }`}
                        />
                        {item.count} {item.label}
                    </span>
                ))}
            </div>
        </div>
    </GenericCard>
);

const NO_REFLECTORS: OverviewReflector[] = [];
const NO_CLUSTERS: OverviewCluster[] = [];
const NO_BGP_SESSIONS: BgpSession[] = [];

const EMPTY_COUNTERS: OverviewCounters = {
    reflectors: { total: 0, online: 0, offline: 0 },
    clusters: { total: 0, online: 0, degraded: 0, offline: 0 },
    bgp: { total: 0, established: 0, down: 0, idle: 0, noData: 0 },
};

const DEFAULT_ROWS_PER_PAGE = 10;
const MAX_FETCH_LIMIT = 50;

/** One table's page of rows plus the tenant-wide counters every overview response carries. */
interface OverviewSection<T> {
    items: T[];
    counters: OverviewCounters | null;
}

/**
 * One overview table, cached per tenant, type and page: coming back to the overview (a
 * view switch, the menu, Back) paints the last answer at once and refreshes it underneath.
 * A search fetches every row (up to the endpoint's limit) so the client-side filter spans
 * the whole dataset; otherwise only the current page.
 */
function useOverviewSection<T>(
    type: OverviewType,
    shaper: (items: unknown[]) => T[],
    page: number,
    rowsPerPage: number,
    hasSearch: boolean,
) {
    const { tenantId } = useAppSelector((state) => state.selection);
    const { isAuthenticated, isLoading: isAuthLoading, getAccessTokenSilently } = useAuth0();
    const tokenRef = useRef(getAccessTokenSilently);
    tokenRef.current = getAccessTokenSilently;

    const limit = hasSearch ? MAX_FETCH_LIMIT : rowsPerPage;
    const requestedPage = hasSearch ? 1 : page;
    const key =
        isAuthLoading || !isAuthenticated || !tenantId
            ? null
            : overviewSectionCacheKey(tenantId, type, limit, requestedPage);

    return useCachedFetch<OverviewSection<T>>(key, async () => {
        try {
            const token = await tokenRef.current();
            const res = await fetchOverview(token, tenantId, type, limit, requestedPage);
            const data = res.data?.data ?? res.data;
            const items = data?.[type]?.items ?? data?.items ?? [];
            return { items: shaper(items), counters: data?.counters ?? null };
        } catch {
            throw new Error(`Failed to load ${type}.`);
        }
    });
}

/* ------------------------------------------------------------------ */
/*  Main Page                                                          */
/* ------------------------------------------------------------------ */

const OverviewPage: React.FC = () => {
    /* ---------- Pagination state per table (server-side mode) ---------- */
    const [refPage, setRefPage] = useState(1);
    const [refRowsPerPage, setRefRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

    const [clPage, setClPage] = useState(1);
    const [clRowsPerPage, setClRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

    const [bgpPage, setBgpPage] = useState(1);
    const [bgpRowsPerPage, setBgpRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

    /* ---------- Search filters (client-side) ---------- */
    const [refFilters, setRefFilters] = useState<ParsedFilter[]>([]);
    const [clFilters, setClFilters] = useState<ParsedFilter[]>([]);
    const [bgpFilters, setBgpFilters] = useState<ParsedFilter[]>([]);

    const hasRefSearch = refFilters.length > 0;
    const hasClSearch = clFilters.length > 0;
    const hasBgpSearch = bgpFilters.length > 0;

    /* ---------- Table data: refetched on pagination or search changes ---------- */
    const reflectorSection = useOverviewSection("reflectors", shapeReflectors, refPage, refRowsPerPage, hasRefSearch);
    const clusterSection = useOverviewSection("clusters", shapeClusters, clPage, clRowsPerPage, hasClSearch);
    const bgpSection = useOverviewSection("bgp", shapeBgpSessions, bgpPage, bgpRowsPerPage, hasBgpSearch);

    const reflectors = reflectorSection.data?.items ?? NO_REFLECTORS;
    const clusters = clusterSection.data?.items ?? NO_CLUSTERS;
    const bgpSessions = bgpSection.data?.items ?? NO_BGP_SESSIONS;
    const loadingReflectors = reflectorSection.isLoading;
    const loadingClusters = clusterSection.isLoading;
    const loadingBgp = bgpSection.isLoading;
    const isRefreshing = reflectorSection.isRefreshing || clusterSection.isRefreshing || bgpSection.isRefreshing;

    // Every response carries the same tenant-wide counters: take whichever section has them.
    const counters =
        reflectorSection.data?.counters ??
        clusterSection.data?.counters ??
        bgpSection.data?.counters ??
        EMPTY_COUNTERS;

    /* ---------- Pagination handlers ---------- */
    const handleRefPageChange = useCallback((p: number) => setRefPage(p), []);
    const handleRefRowsChange = useCallback((r: number) => { setRefRowsPerPage(r); setRefPage(1); }, []);

    const handleClPageChange = useCallback((p: number) => setClPage(p), []);
    const handleClRowsChange = useCallback((r: number) => { setClRowsPerPage(r); setClPage(1); }, []);

    const handleBgpPageChange = useCallback((p: number) => setBgpPage(p), []);
    const handleBgpRowsChange = useCallback((r: number) => { setBgpRowsPerPage(r); setBgpPage(1); }, []);

    /* ---------- Client-side filtered data (used when search is active) ---------- */
    const filteredReflectors = useMemo(() => filterRows(reflectors, refFilters), [reflectors, refFilters]);
    const filteredClusters = useMemo(() => filterRows(clusters, clFilters), [clusters, clFilters]);
    const filteredBgpSessions = useMemo(() => filterRows(bgpSessions, bgpFilters), [bgpSessions, bgpFilters]);

    /* ---------- Counter-based stats ---------- */
    const rc = counters.reflectors;
    const cc = counters.clusters;
    const bc = counters.bgp;

    const reflectorCols = useReflectorColumns();
    const clusterCols = useClusterColumns();
    const bgpSessionCols = useBgpSessionColumns();

    return (
        <div className="relative flex flex-col gap-4 p-1">
            <RefreshingIndicator active={isRefreshing} />
            {/* Summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <SummaryCard
                    title="Reflectors"
                    count={rc.total}
                    items={[
                        ...(rc.online > 0 ? [{ label: "online", count: rc.online, variant: "success" as const }] : []),
                        ...(rc.offline > 0 ? [{ label: "offline", count: rc.offline, variant: "error" as const }] : []),
                    ]}
                />
                <SummaryCard
                    title="Clusters"
                    count={cc.total}
                    items={[
                        ...(cc.online > 0 ? [{ label: "healthy", count: cc.online, variant: "success" as const }] : []),
                        ...(cc.degraded > 0 ? [{ label: "degraded", count: cc.degraded, variant: "warning" as const }] : []),
                        ...(cc.offline > 0 ? [{ label: "offline", count: cc.offline, variant: "error" as const }] : []),
                    ]}
                />
                <SummaryCard
                    title="BGP sessions"
                    count={bc.total}
                    items={[
                        ...(bc.established > 0 ? [{ label: "established", count: bc.established, variant: "success" as const }] : []),
                        ...((bc.idle ?? 0) > 0 ? [{ label: "idle", count: bc.idle ?? 0, variant: "neutral" as const }] : []),
                        ...(bc.down > 0 ? [{ label: "down", count: bc.down, variant: "error" as const }] : []),
                        ...((bc.noData ?? 0) > 0 ? [{ label: "no data", count: bc.noData ?? 0, variant: "neutral" as const }] : []),
                    ]}
                />
            </div>

            {/* Reflectors + Clusters tables side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Reflectors */}
                <GenericCard isCollapsible={false}>
                    <div className="flex items-center justify-between px-4 pt-4 pb-2">
                        <h3 className="text-sm font-semibold text-semantic-text-primary">Reflectors</h3>
                        <GenericTag label={`${rc.total} total`} variant="neutral" size="small" rounded="full" />
                    </div>
                    <div className="px-4 pb-2">
                        <GenericSearchbar
                            placeholder="Find by attribute or value (case sensitive)..."
                            onFiltersChange={setRefFilters}
                        />
                    </div>
                    <DataStateView
                        loading={loadingReflectors}
                        error={reflectorSection.error}
                        isEmpty={!loadingReflectors && !reflectorSection.error && reflectors.length === 0}
                        onRetry={reflectorSection.reload}
                        loadingContent={<TableSkeleton columns={5} rows={6} className="mx-4 mb-4" />}
                    >
                        {hasRefSearch ? (
                            <GenericTable<OverviewReflector>
                                columns={reflectorCols}
                                data={filteredReflectors}
                                rowKey={(row) => row.id}
                                initialRowsPerPage={refRowsPerPage}
                                rowsPerPageOptions={[10, 25, 50]}
                                width="100%"
                                tableClassName="bg-semantic-bg-primary"
                                maxTableBodyHeight="400px"
                            />
                        ) : (
                            <GenericTable<OverviewReflector>
                                columns={reflectorCols}
                                data={reflectors}
                                rowKey={(row) => row.id}
                                serverSide
                                totalCount={rc.total}
                                currentPage={refPage}
                                rowsPerPage={refRowsPerPage}
                                onPageChange={handleRefPageChange}
                                onRowsPerPageChange={handleRefRowsChange}
                                rowsPerPageOptions={[10, 25, 50]}
                                loading={loadingReflectors}
                                width="100%"
                                tableClassName="bg-semantic-bg-primary"
                                maxTableBodyHeight="400px"
                            />
                        )}
                    </DataStateView>
                </GenericCard>

                {/* Clusters */}
                <GenericCard isCollapsible={false}>
                    <div className="flex items-center justify-between px-4 pt-4 pb-2">
                        <h3 className="text-sm font-semibold text-semantic-text-primary">Clusters</h3>
                        <GenericTag label={`${cc.total} clusters`} variant="neutral" size="small" rounded="full" />
                    </div>
                    <div className="px-4 pb-2">
                        <GenericSearchbar
                            placeholder="Find by attribute or value (case sensitive)..."
                            onFiltersChange={setClFilters}
                        />
                    </div>
                    <DataStateView
                        loading={loadingClusters}
                        error={clusterSection.error}
                        isEmpty={!loadingClusters && !clusterSection.error && clusters.length === 0}
                        onRetry={clusterSection.reload}
                        loadingContent={<TableSkeleton columns={5} rows={4} className="mx-4 mb-4" />}
                    >
                        {hasClSearch ? (
                            <GenericTable<OverviewCluster>
                                columns={clusterCols}
                                data={filteredClusters}
                                rowKey={(row) => row.id}
                                initialRowsPerPage={clRowsPerPage}
                                rowsPerPageOptions={[10, 25, 50]}
                                width="100%"
                                tableClassName="bg-semantic-bg-primary"
                                maxTableBodyHeight="400px"
                            />
                        ) : (
                            <GenericTable<OverviewCluster>
                                columns={clusterCols}
                                data={clusters}
                                rowKey={(row) => row.id}
                                serverSide
                                totalCount={cc.total}
                                currentPage={clPage}
                                rowsPerPage={clRowsPerPage}
                                onPageChange={handleClPageChange}
                                onRowsPerPageChange={handleClRowsChange}
                                rowsPerPageOptions={[10, 25, 50]}
                                loading={loadingClusters}
                                width="100%"
                                tableClassName="bg-semantic-bg-primary"
                                maxTableBodyHeight="400px"
                            />
                        )}
                    </DataStateView>
                </GenericCard>
            </div>

            {/* BGP sessions status — full width */}
            <GenericCard isCollapsible={false}>
                <div className="flex items-center justify-between px-4 pt-4 pb-2">
                    <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-semantic-text-primary">BGP sessions status</h3>
                        {bc.established > 0 && (
                            <GenericTag label={`${bc.established} up`} variant="success" size="small" rounded="full" />
                        )}
                        {(bc.idle ?? 0) > 0 && (
                            <GenericTag label={`${bc.idle} idle`} variant="neutral" size="small" rounded="full" />
                        )}
                        {bc.down > 0 && (
                            <GenericTag label={`${bc.down} down`} variant="error" size="small" rounded="full" />
                        )}
                    </div>
                    <GenericTag label={`${bc.total} total`} variant="neutral" size="small" rounded="full" />
                </div>
                <div className="px-4 pb-2">
                    <GenericSearchbar
                        placeholder="Find by attribute or value (case sensitive)..."
                        onFiltersChange={setBgpFilters}
                    />
                </div>
                <DataStateView
                    loading={loadingBgp}
                    error={bgpSection.error}
                    isEmpty={!loadingBgp && !bgpSection.error && bgpSessions.length === 0}
                    onRetry={bgpSection.reload}
                    loadingContent={<TableSkeleton columns={7} rows={4} className="mx-4 mb-4" />}
                >
                    {hasBgpSearch ? (
                        <GenericTable<BgpSession>
                            columns={bgpSessionCols}
                            data={filteredBgpSessions}
                            rowKey={(row) => row.id}
                            initialRowsPerPage={bgpRowsPerPage}
                            rowsPerPageOptions={[10, 25, 50]}
                            width="100%"
                            tableClassName="bg-semantic-bg-primary"
                            maxTableBodyHeight="400px"
                        />
                    ) : (
                        <GenericTable<BgpSession>
                            columns={bgpSessionCols}
                            data={bgpSessions}
                            rowKey={(row) => row.id}
                            serverSide
                            totalCount={bc.total}
                            currentPage={bgpPage}
                            rowsPerPage={bgpRowsPerPage}
                            onPageChange={handleBgpPageChange}
                            onRowsPerPageChange={handleBgpRowsChange}
                            rowsPerPageOptions={[10, 25, 50]}
                            loading={loadingBgp}
                            width="100%"
                            tableClassName="bg-semantic-bg-primary"
                            maxTableBodyHeight="400px"
                        />
                    )}
                </DataStateView>
            </GenericCard>
        </div>
    );
};

export default OverviewPage;
